import { Box, Button } from '@mui/material';
import { HeaderBar } from 'components/HeaderBar';
import PageLayout from 'components/PageLayout';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { OrganizationMembers } from 'features/users/components/OrganizationMembers';
import useScrollPosition from 'hooks/useScrollPosition';
import { defer } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import { useAuthState } from 'state/useAuthState';

export const OrganizationDetails = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    permissions: { canManageOrganization },
  } = useAuthState();
  const location = useLocation();
  const history = useHistory();

  const returnRoute = location.state?.fromRoute || '/';

  const containerRef = useRef<Element>();
  const { handleScroll, resetScrollPosition } = useScrollPosition(
    containerRef,
    'organization-details',
  );

  useEffect(() => {
    const unblock = history.block((promptLocation) => {
      // @ts-expect-error pathname does exist on promptLocation
      const newLocationPathname = promptLocation.pathname;
      if (!newLocationPathname.includes('organization/members')) {
        // Defer scroll reset to ensure it runs after navigation completes.
        // Without deferring, React may batch this with navigation updates,
        // causing Recoil state changes (like scroll position) to be skipped or ignored.
        defer(() => resetScrollPosition());
      }
      return true;
    });

    return () => {
      unblock();
    };
  }, [history, resetScrollPosition]);

  if (!canManageOrganization) {
    return <Redirect to="/" />;
  }

  return (
    <PageLayout data-cname="OrganizationDetails">
      {{
        content: (
          <>
            <Helmet>
              <title>{t('organization.title')}</title>
            </Helmet>
            <SimplePageLayout
              disableGutters
              ref={containerRef}
              onScroll={handleScroll}
            >
              <Box sx={{ p: 2 }}>
                <Button
                  size="large"
                  fullWidth
                  component={Link}
                  to="/organization/members/create"
                >
                  {t('organization.addMember')}
                </Button>
              </Box>
              <OrganizationMembers />
            </SimplePageLayout>
          </>
        ),
        headerBar: (
          <HeaderBar title={t('organization.title')} action={returnRoute} />
        ),
      }}
    </PageLayout>
  );
};
